<div class="space-y-6">
  @if (successMessage) {
    <div hlmAlert>
      <ng-icon name="lucideCircleCheck" />
      <p hlmAlertTitle>{{ successMessage }}</p>
    </div>
  }
  @if (error) {
    <div hlmAlert variant="destructive">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>{{ error }}</p>
    </div>
  }

  @if (isLoading && !selectedGroup) {
    <hlm-skeleton class="h-40 w-full rounded-xl" />
  }

  <!-- ═══════════ Main overview ═══════════ -->
  @if (!isLoading) {
    @if (groups.length === 0 && !selectedGroup && !error) {
      <hlm-empty class="py-10">
        <div hlmEmptyHeader>
          <div hlmEmptyMedia variant="icon"><ng-icon name="lucideUsers" /></div>
          <p hlmEmptyTitle>Du bist in keinem Netzwerk</p>
          @if (!currentCompany?.is_member) {
            <p hlmEmptyDescription>
              Aktiviere den Netzwerk-Modus, um Geschäfte als Mitglieder einzuladen.
            </p>
          }
        </div>
        @if (!currentCompany?.is_member) {
          <a hlmBtn [routerLink]="['/settings']" [queryParams]="{ tab: 'verbund' }">
            Zu den Netzwerk-Einstellungen
          </a>
        }
      </hlm-empty>
    }

    @if (groups.length > 0 && selectedGroup) {
      <!-- Network overview: the Zentrale and the network at a glance -->
      <section hlmCard>
        <div hlmCardContent class="flex flex-col gap-4 sm:flex-row sm:items-start">
          <hlm-avatar class="size-14 rounded-lg">
            @if (imageUrl(selectedGroup.central_company_logo_image_path); as url) {
              <img hlmAvatarImage class="object-contain" [src]="url" [alt]="selectedGroup.name" />
            }
            <span hlmAvatarFallback class="rounded-lg">{{ initials(selectedGroup.name) }}</span>
          </hlm-avatar>

          <div class="min-w-0 flex-1 space-y-2">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-lg font-semibold">{{ selectedGroup.name }}</h2>
              <span hlmBadge variant="secondary">Zentrale</span>
              @if (isCentral(selectedGroup)) {
                <span hlmMuted class="text-xs">(Du)</span>
              }
            </div>
            <ul class="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-sm">
              @if (selectedGroup.central_company_address; as address) {
                <li class="flex items-center gap-1.5">
                  <ng-icon name="lucideMapPin" class="shrink-0" />{{ address }}
                </li>
              }
              @if (selectedGroup.central_company_website; as website) {
                <li class="flex items-center gap-1.5">
                  <ng-icon name="lucideGlobe" class="shrink-0" />
                  <a
                    class="hover:text-foreground underline-offset-4 hover:underline"
                    [href]="websiteHref(website)"
                    target="_blank"
                    rel="noopener">
                    {{ websiteLabel(website) }}
                  </a>
                </li>
              }
              @if (selectedGroup.central_company_email; as email) {
                <li class="flex items-center gap-1.5">
                  <ng-icon name="lucideMail" class="shrink-0" />
                  <a
                    class="hover:text-foreground underline-offset-4 hover:underline"
                    [href]="'mailto:' + email"
                    >{{ email }}</a
                  >
                </li>
              }
              @if (selectedGroup.central_company_phone; as phone) {
                <li class="flex items-center gap-1.5">
                  <ng-icon name="lucidePhone" class="shrink-0" />
                  <a
                    class="hover:text-foreground underline-offset-4 hover:underline"
                    [href]="'tel:' + phone"
                    >{{ phone }}</a
                  >
                </li>
              }
            </ul>
          </div>

          <dl class="flex shrink-0 gap-6 sm:text-right">
            <div>
              <dd class="text-2xl font-semibold tabular-nums">
                {{ getMemberCount(selectedGroup) }}
              </dd>
              <dt hlmMuted class="text-xs">
                {{ getMemberCount(selectedGroup) === 1 ? 'Mitglied' : 'Mitglieder' }}
              </dt>
            </div>
            @if (isCentral(selectedGroup) && getPendingMembers(selectedGroup).length > 0) {
              <div>
                <dd class="text-2xl font-semibold tabular-nums">
                  {{ getPendingMembers(selectedGroup).length }}
                </dd>
                <dt hlmMuted class="text-xs">Warten auf Freigabe</dt>
              </div>
            }
          </dl>
        </div>

        <!-- The member's own standing in its network -->
        @if (!isCentral(selectedGroup) && currentCompany; as me) {
          <div
            hlmCardFooter
            class="flex-col items-start gap-3 border-t sm:flex-row sm:items-center">
            <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <span class="font-medium">Dein Status</span>
              @switch (myStatus(me)) {
                @case ('active') {
                  <span hlmBadge variant="solid">Aktives Mitglied</span>
                  @if (myMembership(selectedGroup); as mine) {
                    <span hlmMuted>seit {{ memberSince(mine.joined_at) }}</span>
                  }
                }
                @case ('waiting') {
                  <span hlmBadge variant="outline">Wartet auf Freigabe</span>
                  <span hlmMuted>{{ selectedGroup.name }} prüft deine Registrierung.</span>
                }
                @case ('incomplete') {
                  <span hlmBadge variant="outline">Registrierung unvollständig</span>
                }
                @default {
                  <span hlmBadge variant="destructive">Kein aktives Mitglied</span>
                }
              }
              <span class="flex items-center gap-1.5">
                <ng-icon name="lucideLandmark" class="text-muted-foreground shrink-0" />
                @if (me.has_bank_account) {
                  <span hlmMuted>Auszahlung eingerichtet</span>
                } @else {
                  <span hlmBadge variant="destructive">IBAN fehlt</span>
                }
                @if (me.pending_bank_iban_masked) {
                  <span hlmMuted>· Neue IBAN wartet auf deine Bestätigung per E-Mail</span>
                }
              </span>
            </div>
            <div class="flex flex-wrap gap-2">
              @if (myStatus(me) === 'incomplete') {
                <a hlmBtn size="sm" routerLink="/onboarding">Registrierung abschließen</a>
              } @else if (!me.has_bank_account) {
                <a
                  hlmBtn
                  size="sm"
                  [routerLink]="['/settings']"
                  [queryParams]="{ tab: 'bank-account' }">
                  IBAN hinterlegen
                </a>
              }
              <a hlmBtn variant="outline" size="sm" routerLink="/payouts">
                Auszahlungen
                <ng-icon name="lucideArrowRight" data-icon="inline-end" />
              </a>
            </div>
          </div>
        }
      </section>

      <!-- Invite bar (central only) -->
      @if (isCentral(selectedGroup)) {
        <div class="space-y-2">
          <div class="flex flex-wrap items-center gap-2">
            <input
              hlmInput
              type="email"
              class="min-w-56 flex-1"
              placeholder="E-Mail-Adresse eingeben…"
              [(ngModel)]="inviteEmail"
              (keyup.enter)="inviteMember(selectedGroup)" />
            <button
              hlmBtn
              [disabled]="inviting || !inviteEmail.trim()"
              (click)="inviteMember(selectedGroup)">
              @if (inviting) {
                <hlm-spinner />
              }
              Per E-Mail einladen
            </button>
            <button
              hlmBtn
              variant="outline"
              [disabled]="creatingLink"
              [hlmTooltip]="'Registrierungslink für Mitglieder kopieren (30 Tage gültig, für beliebig viele Mitglieder)'"
              (click)="createInviteLink(selectedGroup)">
              @if (creatingLink) {
                <hlm-spinner />
              } @else {
                <ng-icon name="lucideLink" />
              }
              Link kopieren
            </button>
          </div>

          @if (freshLink) {
            <div hlmItem variant="muted" size="sm">
              <div hlmItemContent class="min-w-0">
                <span hlmItemTitle>Einladungslink kopiert</span>
                <!-- The whole link stays readable: it scrolls sideways instead of being cut. -->
                <code
                  class="text-muted-foreground block overflow-x-auto pb-1 font-mono text-sm whitespace-nowrap select-all">
                  {{ buildLinkUrl(freshLink.token) }}
                </code>
              </div>
              <div hlmItemActions class="gap-1">
                <button
                  hlmBtn
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Hinweis schließen"
                  (click)="dismissFreshLink()">
                  <ng-icon name="lucideX" />
                </button>
              </div>
            </div>
          }
        </div>
      }

      <!-- Registrations waiting for approval (Zentrale only) -->
      @if (isCentral(selectedGroup) && getPendingMembers(selectedGroup).length > 0) {
        <section hlmCard>
          <div hlmCardHeader>
            <h3 hlmCardTitle>Warten auf Freigabe</h3>
            <p hlmCardDescription>
              Prüfe Name und Adresse, bevor du ein Geschäft in dein Netzwerk aufnimmst.
            </p>
          </div>
          <!-- A list, not a table: on a phone a row stacks, so the buttons stay visible
               instead of scrolling out of view. -->
          <ul hlmCardContent class="divide-y">
            @for (member of getPendingMembers(selectedGroup); track member.company_id) {
              <li class="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 md:flex-row md:items-center">
                <div class="min-w-0 flex-1 space-y-1">
                  <ng-container
                    [ngTemplateOutlet]="memberIdentity"
                    [ngTemplateOutletContext]="{ $implicit: member }" />
                  @if (member.company_email) {
                    <p hlmMuted class="truncate ps-12 text-xs">{{ member.company_email }}</p>
                  }
                </div>
                <div class="flex flex-wrap gap-1">
                  <span hlmBadge variant="outline">
                    {{ canApprove(member) ? 'Bereit zur Freigabe' : 'Registrierung läuft' }}
                  </span>
                  @if (!hasPayoutAccount(member)) {
                    <span hlmBadge variant="destructive">IBAN fehlt</span>
                  }
                </div>
                <div class="flex gap-2">
                  <button
                    hlmBtn
                    size="sm"
                    class="flex-1 md:flex-none"
                    [disabled]="approvingMemberId === member.company_id || !canApprove(member)"
                    [hlmTooltip]="
                      canApprove(member)
                        ? ''
                        : 'Freigabe möglich, sobald das Mitglied seine Registrierung abgeschlossen hat'
                    "
                    (click)="approveMember(selectedGroup, member.company_id, member.company_name)">
                    Freigeben
                  </button>
                  <button
                    hlmBtn
                    variant="outline"
                    size="sm"
                    class="flex-1 md:flex-none"
                    [disabled]="rejectingMemberId === member.company_id"
                    (click)="rejectMember(selectedGroup, member.company_id, member.company_name)">
                    Ablehnen
                  </button>
                </div>
              </li>
            }
          </ul>
        </section>
      }

      <!-- The approved members: every member sees them (social proof) -->
      <section hlmCard>
        <div hlmCardHeader>
          <h3 hlmCardTitle>Mitglieder</h3>
          <p hlmCardDescription>
            @if (isCentral(selectedGroup)) {
              Die Geschäfte, bei denen deine Gutscheine eingelöst werden können.
            } @else {
              Bei diesen Geschäften können Gutscheine von {{ selectedGroup.name }} eingelöst werden.
            }
          </p>
        </div>
        <div hlmTableContainer>
          <table hlmTable>
            <thead hlmTHead>
              <tr hlmTr>
                <th hlmTh>Geschäft</th>
                <th hlmTh class="hidden sm:table-cell">Mitglied seit</th>
                @if (isCentral(selectedGroup)) {
                  <th hlmTh>Bankverbindung</th>
                  <th hlmTh class="hidden lg:table-cell">Kontakt</th>
                }
              </tr>
            </thead>
            <tbody hlmTBody>
              @for (member of getActiveMembers(selectedGroup); track member.company_id) {
                <tr hlmTr [class.bg-muted/50]="member.company_id === currentCompanyId">
                  <td hlmTd>
                    <ng-container
                      [ngTemplateOutlet]="memberIdentity"
                      [ngTemplateOutletContext]="{ $implicit: member }" />
                  </td>
                  <td hlmTd class="text-muted-foreground hidden sm:table-cell">
                    {{ memberSince(member.joined_at) }}
                  </td>
                  @if (isCentral(selectedGroup)) {
                    <td hlmTd>
                      <span
                        hlmBadge
                        [variant]="hasPayoutAccount(member) ? 'outline' : 'destructive'">
                        {{ hasPayoutAccount(member) ? 'Hinterlegt' : 'Fehlt' }}
                      </span>
                    </td>
                    <td hlmTd class="text-muted-foreground hidden lg:table-cell">
                      {{ member.company_email || '–' }}
                    </td>
                  }
                </tr>
              } @empty {
                <tr hlmTr>
                  <td
                    hlmTd
                    [attr.colspan]="isCentral(selectedGroup) ? 4 : 2"
                    class="text-muted-foreground py-6 text-center">
                    @if (isCentral(selectedGroup)) {
                      Noch keine Mitglieder. Lade Geschäfte per E-Mail oder Link ein.
                    } @else {
                      Noch keine weiteren Mitglieder.
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>

      <!-- Name, address and website of a member -->
      <ng-template #memberIdentity let-member>
        <div class="flex items-center gap-3">
          <hlm-avatar class="size-9 rounded-md">
            @if (imageUrl(member.company_logo_image_path); as url) {
              <img
                hlmAvatarImage
                class="object-contain"
                [src]="url"
                [alt]="member.company_name || ''" />
            }
            <span hlmAvatarFallback class="rounded-md text-xs">{{
              initials(member.company_name)
            }}</span>
          </hlm-avatar>
          <div class="min-w-0">
            <p class="truncate font-medium">
              {{ member.company_name || member.company_id }}
              @if (member.company_id === currentCompanyId) {
                <span hlmMuted class="ms-1 text-xs font-normal">(Du)</span>
              }
            </p>
            @if (member.company_address) {
              <p hlmMuted class="truncate text-xs">{{ member.company_address }}</p>
            }
            @if (member.company_website) {
              <a
                class="text-muted-foreground hover:text-foreground truncate text-xs underline-offset-4 hover:underline"
                [href]="websiteHref(member.company_website)"
                target="_blank"
                rel="noopener">
                {{ websiteLabel(member.company_website) }}
              </a>
            }
          </div>
        </div>
      </ng-template>
    }
  }
</div>
